'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect } from 'react';
import { cn } from '../../utils/cn';
import { useTheme } from '../../contexts/theme-context';
import { AlertTriangle, CheckCircle, Info, XCircle, HelpCircle, Trash2, X, } from 'lucide-react';
const variantConfig = {
    info: {
        icon: _jsx(Info, { className: "h-6 w-6" }),
        iconBg: 'bg-blue-100 dark:bg-blue-900/30',
        iconColor: 'text-blue-600 dark:text-blue-400',
        confirmBg: 'bg-blue-600 dark:bg-blue-500',
        confirmHover: 'hover:bg-blue-700 dark:hover:bg-blue-600',
    },
    warning: {
        icon: _jsx(AlertTriangle, { className: "h-6 w-6" }),
        iconBg: 'bg-yellow-100 dark:bg-yellow-900/30',
        iconColor: 'text-yellow-600 dark:text-yellow-400',
        confirmBg: 'bg-yellow-600 dark:bg-yellow-500',
        confirmHover: 'hover:bg-yellow-700 dark:hover:bg-yellow-600',
    },
    danger: {
        icon: _jsx(XCircle, { className: "h-6 w-6" }),
        iconBg: 'bg-red-100 dark:bg-red-900/30',
        iconColor: 'text-red-600 dark:text-red-400',
        confirmBg: 'bg-red-600 dark:bg-red-500',
        confirmHover: 'hover:bg-red-700 dark:hover:bg-red-600',
    },
    success: {
        icon: _jsx(CheckCircle, { className: "h-6 w-6" }),
        iconBg: 'bg-green-100 dark:bg-green-900/30',
        iconColor: 'text-green-600 dark:text-green-400',
        confirmBg: 'bg-green-600 dark:bg-green-500',
        confirmHover: 'hover:bg-green-700 dark:hover:bg-green-600',
    },
    delete: {
        icon: _jsx(Trash2, { className: "h-6 w-6" }),
        iconBg: 'bg-red-100 dark:bg-red-900/30',
        iconColor: 'text-red-600 dark:text-red-400',
        confirmBg: 'bg-red-600 dark:bg-red-500',
        confirmHover: 'hover:bg-red-700 dark:hover:bg-red-600',
    },
    confirm: {
        icon: _jsx(HelpCircle, { className: "h-6 w-6" }),
        iconBg: 'bg-primary-100 dark:bg-primary-900/30',
        iconColor: 'text-primary-600 dark:text-primary-400',
        confirmBg: 'bg-primary-600 dark:bg-primary-500',
        confirmHover: 'hover:bg-primary-700 dark:hover:bg-primary-600',
    },
};
export const ConfirmDialog = ({ isOpen, onClose, onConfirm, title, message, variant = 'confirm', icon, confirmText, cancelText = 'Cancel', showCancel = true, loading = false, closeOnOverlayClick = true, closeOnEscape = true, size = 'md', className, }) => {
    const { theme } = useTheme();
    const isCompact = theme?.layoutDensity === 'compact';
    // Handle escape key
    useEffect(() => {
        if (!closeOnEscape || !isOpen)
            return;
        const handleEscape = (e) => {
            if (e.key === 'Escape' && !loading) {
                onClose();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, closeOnEscape, onClose, loading]);
    // Prevent body scroll
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        }
        else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);
    const handleOverlayClick = useCallback((e) => {
        if (closeOnOverlayClick && e.target === e.currentTarget && !loading) {
            onClose();
        }
    }, [closeOnOverlayClick, onClose, loading]);
    const handleConfirm = useCallback(async () => {
        if (loading)
            return;
        await onConfirm();
    }, [loading, onConfirm]);
    if (!isOpen)
        return null;
    const config = variantConfig[variant];
    const defaultConfirmText = variant === 'delete' ? 'Delete' : variant === 'danger' ? 'Confirm' : 'OK';
    // Size classes
    const sizeClasses = {
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-lg',
    };
    const iconSizeClasses = {
        sm: isCompact ? 'h-10 w-10' : 'h-12 w-12',
        md: isCompact ? 'h-12 w-12' : 'h-14 w-14',
        lg: isCompact ? 'h-14 w-14' : 'h-16 w-16',
    };
    const buttonSizeClasses = {
        sm: isCompact ? 'h-7 px-3 text-xs' : 'h-8 px-3 text-sm',
        md: isCompact ? 'h-8 px-4 text-sm' : 'h-9 px-4 text-sm',
        lg: isCompact ? 'h-9 px-5 text-base' : 'h-10 px-6 text-base',
    };
    return (_jsxs("div", { className: "fixed inset-0 z-50 flex items-center justify-center", children: [_jsx("div", { className: "fixed inset-0 bg-black/50 dark:bg-black/70 transition-opacity", onClick: handleOverlayClick }), _jsxs("div", { className: cn('relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full mx-4', 'border border-gray-200 dark:border-gray-700', 'transform transition-all', sizeClasses[size], className), children: [_jsx("button", { onClick: onClose, disabled: loading, className: cn('absolute top-3 right-3 p-1 rounded-md transition-colors', 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300', 'hover:bg-gray-100 dark:hover:bg-gray-700', 'disabled:opacity-50 disabled:cursor-not-allowed'), children: _jsx(X, { className: "h-5 w-5" }) }), _jsxs("div", { className: cn('flex flex-col items-center text-center', isCompact ? 'p-4 pt-6' : 'p-6 pt-8'), children: [_jsx("div", { className: cn('flex items-center justify-center rounded-full', iconSizeClasses[size], config.iconBg), children: _jsx("div", { className: config.iconColor, children: icon || config.icon }) }), _jsx("h3", { className: cn('font-semibold text-gray-900 dark:text-white mt-4', isCompact ? 'text-base' : 'text-lg'), children: title }), _jsx("div", { className: cn('text-gray-600 dark:text-gray-400 mt-2', isCompact ? 'text-sm' : 'text-base'), children: message }), _jsxs("div", { className: cn('flex items-center justify-center gap-3 w-full', isCompact ? 'mt-4' : 'mt-6'), children: [showCancel && (_jsx("button", { onClick: onClose, disabled: loading, className: cn('font-medium rounded-md transition-colors', 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300', 'hover:bg-gray-200 dark:hover:bg-gray-600', 'disabled:opacity-50 disabled:cursor-not-allowed', buttonSizeClasses[size]), children: cancelText })), _jsx("button", { onClick: handleConfirm, disabled: loading, className: cn('font-medium rounded-md transition-colors text-white', 'disabled:opacity-50 disabled:cursor-not-allowed', 'focus:outline-none focus:ring-2 focus:ring-offset-2', config.confirmBg, config.confirmHover, buttonSizeClasses[size]), children: loading ? (_jsxs("span", { className: "flex items-center gap-2", children: [_jsxs("svg", { className: "animate-spin h-4 w-4", fill: "none", viewBox: "0 0 24 24", children: [_jsx("circle", { className: "opacity-25", cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "4" }), _jsx("path", { className: "opacity-75", fill: "currentColor", d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" })] }), "Processing..."] })) : (confirmText || defaultConfirmText) })] })] })] })] }));
};
ConfirmDialog.displayName = 'ConfirmDialog';
